<template>
  <div class="archive__payment-form">
    <div class="archive__email-input-container">
      <label
        for="payment-email"
        class="archive__email-label archive__email-label--flex"
      >
        <div class="archive__email-label-text">Your email address</div>

        <label class="archive__gift-toggle">
          <input
            v-model="form.isGift"
            type="checkbox"
            class="archive__gift-checkbox"
          />
          <span>This is a gift</span>
        </label>
      </label>
      <input
        id="payment-email"
        v-model="form.emailAddress"
        type="email"
        class="archive__email-input"
        placeholder="you@example.com"
        @blur="handleEmailChange"
      />
    </div>
    <div v-if="form.isGift" class="archive__email-input-container">
      <label for="giftee-email" class="archive__email-label">
        The recipient's email address
      </label>
      <input
        id="giftee-email"
        v-model="form.giftRecipientEmailAddress"
        type="email"
        class="archive__email-input"
        placeholder="recipient@example.com"
      />
    </div>
    <div v-if="form.isGift" class="archive__email-input-container">
      <label for="gift-message" class="archive__email-label">
        Gift message (optional)
      </label>
      <textarea
        id="gift-message"
        v-model="form.giftMessage"
        class="archive__email-input"
        placeholder="Add a personal message..."
        rows="3"
      ></textarea>
    </div>
    <div id="payment-element"></div>
    <ActionButton
      :text="formattedPrice ? `Subscribe for ${formattedPrice}` : 'Subscribe'"
      color="green"
      :pending="state.state === 'pending_confirmation'"
      :disabled="isButtonDisabled || state.state === 'pending_confirmation'"
      @click="finalize"
    />
  </div>

  <Teleport to="body">
    <div
      v-if="state.state === 'success'"
      class="archive__modal-overlay"
      @click="state = { state: 'initial' }"
    >
      <div class="archive__modal-container" @click.stop>
        <div class="archive__modal-header">
          <h2 class="archive__modal-title">Welcome to Premium!</h2>
          <button
            type="button"
            class="archive__modal-close"
            @click="state = { state: 'initial' }"
          >
            <CloseIcon class="archive__close-icon" />
          </button>
        </div>
        <div class="archive__modal-content">
          <CheckIcon class="archive__success-icon" />
          <p class="archive__success-message">
            You've successfully upgraded to premium! Get started by exploring
            the archives or managing your subscription.
          </p>
        </div>
        <div class="archive__modal-actions">
          <button
            type="button"
            class="archive__success-button archive__success-button--secondary"
            @click="state = { state: 'initial' }"
          >
            Close
          </button>
          <a
            :href="archiveUrl"
            class="archive__success-button archive__success-button--primary"
          >
            <ArchiveIcon class="archive__button-icon" />
            View Archives
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import {
  type Appearance,
  type LoadActionsSuccess,
  type Stripe,
  loadStripe,
} from "@stripe/stripe-js";
import { computed, onMounted, ref, watch } from "vue";

import { paths as InternalPaths } from "@/autogen/internal-openapi";
import ArchiveIcon from "@/icons/heroicons/archive-box-micro.svg";
import CheckIcon from "@/icons/heroicons/check-circle-micro.svg";
import CloseIcon from "@/icons/heroicons/x-mark-micro.svg";
import { createClient } from "@/lib/openapi";
import { Newsletter } from "@/types/newsletter";
import { debounce } from "@/utils";

import ActionButton from "./Button.vue";

const getAppearance = (): Appearance => {
  const isDarkMode = document.documentElement.classList.contains("dark");

  if (!isDarkMode) {
    return {
      theme: "flat",
      inputs: "condensed",
      labels: "floating",
      variables: {
        fontLineHeight: "1.5",
        borderRadius: "20px",
        colorBackground: "#ffffff",
        accessibleColorOnColorPrimary: "#1f2937",
        spacingUnit: "8px",
        tabSpacing: "5px",
      },
      rules: {
        ".Input": {
          backgroundColor: "#ffffff",
          padding: "6px 12px",
          border: "1px solid #d1d5db",
          color: "#111827",
          fontSize: "14px",
        },
        ".Input:disabled, .Input--invalid:disabled": {
          color: "#9ca3af",
          backgroundColor: "#f9fafb",
        },
        ".Tab": {
          padding: "10px 12px 8px 12px",
          border: "1px solid #d1d5db",
          backgroundColor: "#ffffff",
          color: "#111827",
        },
        ".Tab:hover": {
          backgroundColor: "#f9fafb",
          color: "#111827",
        },
        ".TabIcon": {
          width: "16px",
          fill: "#6b7280",
        },
        ".TabIcon:hover": {
          fill: "#111827",
        },
        ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
          border: "none",
          backgroundColor: "#007bfd",
          color: "#ffffff",
          boxShadow:
            "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
        },
        ".TabIcon--selected,.TabIcon--selected:hover,.TabIcon--selected:focus":
          {
            fill: "#ffffff",
          },
        ".Label": {
          fontSize: "14px",
          color: "#6b7280",
        },
      },
    };
  }

  return {
    theme: "flat",
    inputs: "condensed",
    labels: "floating",
    variables: {
      fontLineHeight: "1.5",
      borderRadius: "20px",
      colorBackground: "oklch(32% 0.013 285.805)",
      accessibleColorOnColorPrimary: "#262626",
      spacingUnit: "8px",
      tabSpacing: "5px",
    },
    rules: {
      ".Input": {
        backgroundColor: "oklch(32% 0.013 285.805)",
        padding: "6px 12px",
        border: "1px solid oklch(44.2% 0.017 285.786)",
        color: "oklch(93.382% 0.006 285.885)",
        fontSize: "14px",
      },
      ".Input:disabled, .Input--invalid:disabled": {
        color: "lightgray",
        backgroundColor: "#262626",
      },
      ".Tab": {
        padding: "10px 12px 8px 12px",
        border: "1px solid oklch(44.2% 0.017 285.786)",
        backgroundColor: "#262626",
        color: "oklch(93.382% 0.006 285.885)",
      },
      ".Tab:hover": {
        backgroundColor: "oklch(37% 0.013 285.805)",
        color: "oklch(93.382% 0.006 285.885)",
      },
      ".TabIcon": {
        width: "16px",
        fill: "oklch(70.888% 0.006 286.033)",
      },
      ".TabIcon:hover": {
        fill: "oklch(93.382% 0.006 285.885)",
      },
      ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
        border: "none",
        backgroundColor: "#007bfd",
        color: "oklch(93.382% 0.006 285.885)",
        boxShadow:
          "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      },
      ".TabIcon--selected,.TabIcon--selected:hover,.TabIcon--selected:focus": {
        fill: "oklch(93.382% 0.006 285.885)",
      },
      ".Label": {
        fontSize: "14px",
        color: "oklch(70.888% 0.006 286.033)",
      },
    },
  };
};

declare const STRIPE_LIVE_PUBLIC_KEY: string;

const props = defineProps<{
  newsletter: Newsletter;
  emailAddress?: string;
}>();

// Actual form elements that the visitor can modify.
const form = ref<{
  emailAddress: string;
  isGift: boolean;
  giftRecipientEmailAddress: string;
  giftMessage: string;
}>({
  emailAddress: props.emailAddress || "",
  isGift: false,
  giftRecipientEmailAddress: "",
  giftMessage: "",
});

// Exogenous state.
const amount = ref<number | null>(null);
const currency = ref<string>("");
const interval = ref<string>("");
const checkoutSessionId = ref<string | null>(null);

// Lastly, overall state machine.
type State =
  | {
      state: "initial" | "loading_stripe" | "success";
    }
  | {
      state: "ready" | "pending_confirmation";
      actions: LoadActionsSuccess;
    }
  | {
      state: "error";
      error: string;
    };

const state = ref<State>({ state: "initial" });

const isButtonDisabled = computed(() => {
  if (!form.value.emailAddress) {
    return true;
  }
  if (form.value.isGift && !form.value.giftRecipientEmailAddress) {
    return true;
  }
  return false;
});

const formattedPrice = computed(() => {
  if (!amount.value || !currency.value) {
    return "";
  }
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.value.toUpperCase(),
  }).format(amount.value / 100);

  if (interval.value) {
    return `${formatted}/${interval.value}`;
  }
  return formatted;
});

const archiveUrl = computed(() => {
  return `/${props.newsletter.username}/archive`;
});

const handleEmailChange = async () => {
  if (!form.value.emailAddress) {
    return;
  }

  if (state.value.state !== "ready") {
    return;
  }

  try {
    // Update the email using Stripe's actions API
    const result = await state.value.actions?.updateEmail(
      form.value.emailAddress
    );

    if (result.type === "error") {
      console.error("Error updating email:", result.error);
      state.value = {
        state: "error",
        error: result.error.message || "Failed to update email.",
      };
    }
  } catch (err) {
    console.error("Error updating email:", err);
    state.value = { state: "error", error: "Failed to update email." };
  }
};

// Initialize Stripe
const mountStripe = async () => {
  let stripeInstance: Stripe | null = null;

  try {
    stripeInstance = await loadStripe(STRIPE_LIVE_PUBLIC_KEY, {
      stripeAccount: props.newsletter.stripe_account,
    });
  } catch {
    state.value = {
      state: "error",
      error: "Failed to initialize payment form.",
    };
    return;
  }

  if (!stripeInstance) {
    state.value = {
      state: "error",
      error: "Failed to initialize payment form.",
    };
    return;
  }

  if (state.value.state === "loading_stripe") {
    return;
  }

  state.value = { state: "loading_stripe" };

  // Create new checkout session

  const response = await createClient<InternalPaths>({
    base: !import.meta.env.VITEST
      ? "/api/emails/api"
      : "http://localhost:80/api/emails/api",
  }).post("/newsletters/{id}/checkout-sessions", {
    params: {
      path: { id: props.newsletter.id },
    },
    body: {
      email_address: form.value.emailAddress,
      gift_message: form.value.giftMessage,
      gift_recipient_email_address: form.value.giftRecipientEmailAddress,
    },
    headers: {
      Authorization: `Token ${props.newsletter.id}`,
    },
  });

  if (response.error) {
    console.error("Failed to create checkout session.", response.error);
    state.value = {
      state: "error",
      error: "Failed to create checkout session.",
    };
    return;
  }
  amount.value = response.data.amount_total;
  currency.value = response.data.currency;
  interval.value =
    (response.data.metadata?.interval as string | undefined) || "";
  checkoutSessionId.value = response.data.id;

  // Create new elements with appearance
  const elements = stripeInstance.initCheckout({
    clientSecret: response.data.client_secret,
    elementsOptions: {
      appearance: getAppearance(),
    },
  });

  // Create and mount new payment element
  elements
    .createPaymentElement({
      layout: "tabs",
      terms: {
        card: "never",
      },
    })
    .mount("#payment-element");

  const loadActionsResult = await elements.loadActions();
  if (loadActionsResult.type === "error") {
    state.value = {
      state: "error",
      error: loadActionsResult.error.message || "Failed to load actions.",
    };
  } else {
    state.value = { state: "ready", actions: loadActionsResult.actions };
  }
};

onMounted(async () => {
  // Check for successful payment return
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get("session_id");
  const accountId = urlParams.get("account_id");

  if (sessionId && accountId) {
    state.value = { state: "success" };
    // Clean up the URL by removing query parameters
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    url.searchParams.delete("account_id");
    window.history.replaceState({}, "", url.toString());
    return;
  }

  await mountStripe();
});

const updateCheckoutSession = async () => {
  if (!checkoutSessionId.value) {
    // No checkout session yet, need to mount
    await mountStripe();
    return;
  }

  if (state.value.state !== "ready") {
    return;
  }

  await createClient<InternalPaths>({
    base: !import.meta.env.VITEST
      ? "/api/emails/api"
      : "http://localhost:80/api/emails/api",
  }).patch("/newsletters/{id}/checkout-sessions/{checkout_session_id}", {
    params: {
      path: {
        id: props.newsletter.id,
        checkout_session_id: checkoutSessionId.value,
      },
    },
    body: {
      metadata: {
        email_address: form.value.emailAddress,
        gift_message: form.value.giftMessage || "",
        gift_recipient_email_address:
          form.value.giftRecipientEmailAddress || "",
      },
    },
    headers: {
      Authorization: `Token ${props.newsletter.id}`,
    },
  });
};

watch(
  form,
  debounce(() => {
    updateCheckoutSession();
  }, 1000),
  {
    deep: true,
  }
);

const finalize = async () => {
  if (state.value.state !== "ready") {
    return;
  }

  state.value = { state: "pending_confirmation", actions: state.value.actions };

  const result = await state.value.actions.confirm({
    returnUrl: window.location.href,
    redirect: "if_required",
    email: form.value.giftRecipientEmailAddress || form.value.emailAddress,
  });
  if (result.type === "error") {
    state.value = {
      state: "error",
      error: result.error.message || "Failed to confirm payment.",
    };
  } else {
    window.location.href = `?session_id=${result.session.id}&account_id=${props.newsletter.stripe_account}`;
  }
};
</script>

<style lang="scss">
@import "./PaymentElement.scss";
</style>
